import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {select, Store} from '@ngrx/store';
import {PartnerApi, StammdatenApi} from 'cargo-hub-ui-customer-api';
import {of} from 'rxjs';
import {catchError, filter, map, mergeMap} from 'rxjs/operators';
import {ErrorService} from '../../cd-common/service/error.service';
import {LanguageService} from '../../services/language/language.service';
import {
  loadAsNumbers,
  loadCustomsProcedures,
  loadEiuCodes,
  loadEwcCodeTypes,
  loadFrankaturen,
  loadFrankaturGebuehren,
  loadFreightAttachment,
  loadFreightSenderNotes,
  loadIsoCountries,
  loadPackagingTypes,
  loadPartners,
  loadRidData,
  loadRidDataSuccess,
  loadRidDataTrigger,
  loadRidEmptyPackaging,
  loadRidSpecialRegulations,
  loadRidVersions,
  loadTransportRestrictions,
  loadUicCountries,
  loadZusatzleistungsArten
} from './master-data-store.actions';
import {
  selectAsNumbersLoadingOrLoaded,
  selectCustomsProceduresLoadingOrLoaded,
  selectEiuCodeLoadingOrLoaded,
  selectFrankaturenLoadingOrLoaded,
  selectFrankaturGebuehrenLoadingOrLoaded,
  selectFreightAttachmentsLoadingOrLoaded,
  selectFreightSenderNotesLoadingOrLoaded,
  selectIsoCountriesLoadingOrLoaded,
  selectPackagingTypesLoadingOrLoaded,
  selectPartnersLoadingOrLoaded,
  selectRidDataLoadingOrLoaded,
  selectRidDataVersionsLoadingOrLoaded,
  selectRidEmptyPackagingLoadingOrLoaded,
  selectSpecialRegulationsLoadingOrLoaded,
  selectTransportRestrictionsLoadingOrLoaded,
  selectEwcCodeTypesLoadingOrLoaded,
  selectUicLaenderLoadingOrLoaded,
  selectZusatzleistungsArtenLoadingOrLoaded
} from './master-data-store.selectors';

@Injectable()
export class MasterDataStoreEffects {
  triggerLoadFreightSenderNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFreightSenderNotes.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectFreightSenderNotesLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadFreightSenderNotes.load())
    )
  );

  loadFreightSenderNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFreightSenderNotes.load),
      mergeMap(() =>
        this.stammdatenApi.getFrachtbriefErklaerungAbsender(this.languageService.lang).pipe(
          map(result => loadFreightSenderNotes.success({senderNotes: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadFreightAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFreightAttachment.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectFreightAttachmentsLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadFreightAttachment.load())
    )
  );

  loadFreightAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFreightAttachment.load),
      mergeMap(() =>
        this.stammdatenApi.getFrachtbriefBeilagen(this.languageService.lang).pipe(
          map(result => loadFreightAttachment.success({freightAttachments: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadUicCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUicCountries.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectUicLaenderLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadUicCountries.load())
    )
  );

  loadUicCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUicCountries.load),
      mergeMap(() =>
        this.stammdatenApi.getUicLaender(this.languageService.lang).pipe(
          map(result => loadUicCountries.success({uicCountries: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadIsoCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadIsoCountries.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectIsoCountriesLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadIsoCountries.load())
    )
  );

  loadIsoCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadIsoCountries.load),
      mergeMap(() =>
        this.stammdatenApi.getIsoLaender(this.languageService.lang).pipe(
          map(result => loadIsoCountries.success({isoCountries: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadAsNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAsNumbers.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectAsNumbersLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadAsNumbers.load())
    )
  );

  loadAsNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAsNumbers.load),
      mergeMap(() =>
        this.stammdatenApi.getAsNummern().pipe(
          map(result => loadAsNumbers.success({asNumbers: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadEiuCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEiuCodes.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectEiuCodeLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadEiuCodes.load())
    )
  );

  loadEiuCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEiuCodes.load),
      mergeMap(() =>
        this.stammdatenApi.getEiuCodes().pipe(
          map(result => loadEiuCodes.success({eiuCodes: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadRidData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRidDataTrigger),
      concatLatestFrom(() => this.store.pipe(select(selectRidDataLoadingOrLoaded))),
      filter(([action, ridLoadingOrLoaded]) => action.ridVersion !== ridLoadingOrLoaded.ridVersion || !ridLoadingOrLoaded.loadingOrLoaded),
      map(([action]) => loadRidData({ridVersion: action.ridVersion}))
    )
  );

  loadRidData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRidData),
      mergeMap(action =>
        this.stammdatenApi.getGefahrgutdaten(action.ridVersion).pipe(
          map(result => loadRidDataSuccess({ridData: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadRidVersions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRidVersions.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectRidDataVersionsLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadRidVersions.load())
    )
  );

  loadRidVersions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRidVersions.load),
      mergeMap(() =>
        this.stammdatenApi.getGefahrgutdatenVorschriftversionen().pipe(
          map(result => loadRidVersions.success({ridVersions: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadRidSpecialRegulations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRidSpecialRegulations.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectSpecialRegulationsLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadRidSpecialRegulations.load())
    )
  );

  loadRidSpecialRegulations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRidSpecialRegulations.load),
      mergeMap(() =>
        this.stammdatenApi.getGefahrdatenSondervorschriften().pipe(
          map(result => loadRidSpecialRegulations.success({ridRegulations: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadRidEmptyPackaging$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRidEmptyPackaging.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectRidEmptyPackagingLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadRidEmptyPackaging.load())
    )
  );

  loadRidEmptyPackaging$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRidEmptyPackaging.load),
      mergeMap(() =>
        this.stammdatenApi.getGefahrgutLeerverpackungen().pipe(
          map(result => loadRidEmptyPackaging.success({ridEmptyPackaging: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadCustomsProcedures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCustomsProcedures.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectCustomsProceduresLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadCustomsProcedures.load())
    )
  );

  loadCustomsProcedures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCustomsProcedures.load),
      mergeMap(() =>
        this.stammdatenApi.getZollverfahren(this.languageService.lang).pipe(
          // return empty list for gracefully fallback (e.g. missing mock data)
          map(result => loadCustomsProcedures.success({customsProcedures: result ?? []})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadFrankaturGebuehren$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFrankaturGebuehren.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectFrankaturGebuehrenLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadFrankaturGebuehren.load())
    )
  );
  loadFrankaturGebuehren$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFrankaturGebuehren.load),
      mergeMap(() =>
        this.stammdatenApi.getFrachtbriefFrankaturenGebuehren(this.languageService.lang).pipe(
          // return empty list for gracefully fallback (e.g. missing mock data)
          map(result => loadFrankaturGebuehren.success({frankaturGebuehren: result ?? []})),
          catchError(err => {
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, 'ERROR_MASTER_DATA_LOAD');
            return of(errorAction);
          })
        )
      )
    )
  );
  triggerLoadFrankaturen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFrankaturen.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectFrankaturenLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadFrankaturen.load())
    )
  );
  loadFrankaturen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFrankaturen.load),
      mergeMap(() =>
        this.stammdatenApi.getFrachtbriefFrankaturen(this.languageService.lang).pipe(
          // return empty list for gracefully fallback (e.g. missing mock data)
          map(result => loadFrankaturen.success({frankaturen: result ?? []})),
          catchError(err => {
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, 'ERROR_MASTER_DATA_LOAD');
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadTransportRestrictions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTransportRestrictions.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectTransportRestrictionsLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadTransportRestrictions.load())
    )
  );

  loadTransportRestrictions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTransportRestrictions.load),
      mergeMap(() =>
        this.stammdatenApi.getBefoerderungsbeschraenkungen(this.languageService.lang).pipe(
          map(result => loadTransportRestrictions.success({transportRestrictions: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadPackagingTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPackagingTypes.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectPackagingTypesLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadPackagingTypes.load())
    )
  );

  loadPackagingTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPackagingTypes.load),
      mergeMap(() =>
        this.stammdatenApi.getVerpackungsarten(undefined, undefined, this.languageService.lang).pipe(
          map(result => loadPackagingTypes.success({packagingTypes: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadEwcCodeTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEwcCodeTypes.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectEwcCodeTypesLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadEwcCodeTypes.load())
    )
  );

  loadEwcCodeTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEwcCodeTypes.load),
      mergeMap(() =>
        this.stammdatenApi.getEwcCodes(this.languageService.lang).pipe(
          map(result => loadEwcCodeTypes.success({ewcCodeTypes: result ?? []})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerPartners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPartners.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectPartnersLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadPartners.load())
    )
  );

  loadPartners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPartners.load),
      mergeMap(() =>
        this.partnerApi.getStammdatenPartner().pipe(
          map(result => loadPartners.success({partners: result})),
          catchError(err => {
            const i18nToken = $localize`:@@ERROR_MASTER_DATA_LOAD:Stammdaten konnten nicht geladen werden.`;
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
            return of(errorAction);
          })
        )
      )
    )
  );

  triggerLoadZusatzleistungsArten$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadZusatzleistungsArten.trigger),
      concatLatestFrom(() => this.store.pipe(select(selectZusatzleistungsArtenLoadingOrLoaded))),
      filter(([, loadingOrLoaded]) => !loadingOrLoaded),
      map(() => loadZusatzleistungsArten.load())
    )
  );
  loadZusatzleistungsArten$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadZusatzleistungsArten.load),
      mergeMap(() =>
        this.stammdatenApi.getTransportauftragZusatzleistungen(this.languageService.lang).pipe(
          // return empty list for gracefully fallback (e.g. missing mock data)
          map(result => loadZusatzleistungsArten.success({zusatzleistungsArten: result ?? []})),
          catchError(err => {
            const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, 'ERROR_MASTER_DATA_LOAD');
            return of(errorAction);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private stammdatenApi: StammdatenApi,
    private languageService: LanguageService,
    private errorService: ErrorService,
    private partnerApi: PartnerApi
  ) {}
}
